import { Checkbox } from '@/ui/base/checkbox';
import {
  PERMISSION_GROUPS,
  ACTION_LABELS,
  type PermissionsMap,
  type PermissionAction,
} from '@/lib/permissionModules';

const ALL_ACTIONS: PermissionAction[] = ['visualizar', 'criar', 'editar', 'excluir'];

interface PermissionMatrixProps {
  permissoes: PermissionsMap;
  onChange?: (permissoes: PermissionsMap) => void;
  readOnly?: boolean;
}

export function PermissionMatrix({ permissoes, onChange, readOnly }: PermissionMatrixProps) {
  const togglePermission = (moduleKey: string, action: PermissionAction) => {
    if (!onChange) return;
    const module = permissoes[moduleKey] || {};
    onChange({ ...permissoes, [moduleKey]: { ...module, [action]: !module[action] } });
  };

  const toggleModuleRow = (moduleKey: string, actions: PermissionAction[]) => {
    if (!onChange) return;
    const current = permissoes[moduleKey] || {};
    const allChecked = actions.every((a) => current[a]);
    const newVal = !allChecked;
    const module: Partial<Record<PermissionAction, boolean>> = {};
    actions.forEach((a) => (module[a] = newVal));
    onChange({ ...permissoes, [moduleKey]: module });
  };

  const toggleColumn = (action: PermissionAction) => {
    if (!onChange) return;
    const allModules = PERMISSION_GROUPS.flatMap((g) => g.modules).filter((m) =>
      m.actions.includes(action),
    );
    const allChecked = allModules.every((m) => permissoes[m.key]?.[action]);
    const newVal = !allChecked;
    const next = { ...permissoes };
    allModules.forEach((m) => {
      next[m.key] = { ...(next[m.key] || {}), [action]: newVal };
    });
    onChange(next);
  };

  const toggleAll = () => {
    if (!onChange) return;
    const allModules = PERMISSION_GROUPS.flatMap((g) => g.modules);
    const allChecked = allModules.every((m) => m.actions.every((a) => permissoes[m.key]?.[a]));
    const newVal = !allChecked;
    const next: PermissionsMap = {};
    allModules.forEach((m) => {
      const modulePerms: Partial<Record<PermissionAction, boolean>> = {};
      m.actions.forEach((a) => (modulePerms[a] = newVal));
      next[m.key] = modulePerms;
    });
    onChange(next);
  };

  const allModules = PERMISSION_GROUPS.flatMap((g) => g.modules);
  const globalAllChecked = allModules.every((m) => m.actions.every((a) => permissoes[m.key]?.[a]));

  return (
    <div className="overflow-x-auto border rounded-lg">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="text-left p-3 font-medium text-muted-foreground min-w-[200px]">
              <div className="flex items-center gap-2">
                {!readOnly && (
                  <Checkbox
                    checked={globalAllChecked}
                    onCheckedChange={() => toggleAll()}
                    aria-label="Selecionar tudo"
                  />
                )}
                <span>Módulo</span>
              </div>
            </th>
            {ALL_ACTIONS.map((action) => (
              <th
                key={action}
                className="text-center p-3 font-medium text-muted-foreground min-w-[90px]"
              >
                <div className="flex flex-col items-center gap-1">
                  {!readOnly && (
                    <Checkbox
                      checked={PERMISSION_GROUPS.flatMap((g) => g.modules)
                        .filter((m) => m.actions.includes(action))
                        .every((m) => permissoes[m.key]?.[action])}
                      onCheckedChange={() => toggleColumn(action)}
                      aria-label={`Selecionar tudo - ${ACTION_LABELS[action]}`}
                    />
                  )}
                  <span className="text-xs">{ACTION_LABELS[action]}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PERMISSION_GROUPS.map((group) => (
            <GroupRows
              key={group.label}
              group={group}
              permissoes={permissoes}
              readOnly={readOnly}
              onTogglePermission={togglePermission}
              onToggleRow={toggleModuleRow}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface GroupRowsProps {
  group: (typeof PERMISSION_GROUPS)[0];
  permissoes: PermissionsMap;
  readOnly?: boolean;
  onTogglePermission: (key: string, action: PermissionAction) => void;
  onToggleRow: (key: string, actions: PermissionAction[]) => void;
}

function GroupRows({
  group,
  permissoes,
  readOnly,
  onTogglePermission,
  onToggleRow,
}: GroupRowsProps) {
  return (
    <>
      <tr className="bg-muted/30">
        <td
          colSpan={5}
          className="p-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
        >
          {group.label}
        </td>
      </tr>
      {group.modules.map((mod) => {
        const modPerms = permissoes[mod.key] || {};
        const rowAllChecked = mod.actions.every((a) => modPerms[a]);
        return (
          <tr key={mod.key} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
            <td className="p-3">
              <div className="flex items-center gap-2">
                {!readOnly && (
                  <Checkbox
                    checked={rowAllChecked}
                    onCheckedChange={() => onToggleRow(mod.key, mod.actions)}
                    aria-label={`Selecionar tudo - ${mod.label}`}
                  />
                )}
                <span className="font-medium">{mod.label}</span>
              </div>
            </td>
            {ALL_ACTIONS.map((action) => (
              <td key={action} className="text-center p-3">
                {mod.actions.includes(action) ? (
                  <div className="flex justify-center">
                    <Checkbox
                      checked={!!modPerms[action]}
                      onCheckedChange={() => onTogglePermission(mod.key, action)}
                      disabled={readOnly}
                      aria-label={`${mod.label} - ${ACTION_LABELS[action]}`}
                    />
                  </div>
                ) : (
                  <span className="text-muted-foreground/30">—</span>
                )}
              </td>
            ))}
          </tr>
        );
      })}
    </>
  );
}
